import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { register, verifyEmail, verifyGroupCode, clearRegistrationData } from '../../actions/authActions';
import { getTermsByProgram } from '../../actions/termsConditionsActions';
import { clearErrors } from '../../actions/errorActions';
import { X } from 'lucide-react';
import ReactDOM from 'react-dom';
import EmailStep from './registration/EmailStep';
import TermsStep from './registration/TermsStep';
import UserDetailsStep from './registration/UserDetailsStep';

class RegisterModal extends Component {
  state = {
    modal: false,
    currentStep: 1,
    msg: null,
    loading: false
  };

  static propTypes = {
    isAuthenticated: PropTypes.bool,
    error: PropTypes.object.isRequired,
    register: PropTypes.func.isRequired,
    verifyEmail: PropTypes.func.isRequired,
    verifyGroupCode: PropTypes.func.isRequired,
    getTermsByProgram: PropTypes.func.isRequired,
    clearErrors: PropTypes.func.isRequired,
    clearRegistrationData: PropTypes.func.isRequired,
    registrationData: PropTypes.object.isRequired,
    registrationTerms: PropTypes.object,
    variant: PropTypes.oneOf(['navbar', 'home'])
  };

  componentDidUpdate(prevProps) {
    const { error, isAuthenticated, registrationData } = this.props;
    
    // Handle noInvitation case - reset loading to allow group code input
    if (registrationData !== prevProps.registrationData && registrationData.noInvitation) {
      this.setState({ loading: false, msg: null });
    }

    if (error !== prevProps.error) {
      if (error.id === 'REGISTER_FAIL' || error.id === 'VERIFY_EMAIL_FAIL') {
        const errorMessage = typeof error.msg === 'string'
          ? error.msg
          : error.msg.msg || error.msg.errors || 'Operation failed';
        // Don't show error if it's just "no invitation" - we handle that differently
        if (!registrationData.noInvitation) {
          this.setState({ msg: errorMessage, loading: false });
        } else {
          this.setState({ loading: false });
        }
      } else {
        this.setState({ msg: null });
      }
    }

    // Move to step 2 or 3 when email verification succeeds
    if (registrationData !== prevProps.registrationData && registrationData.email && !registrationData.noInvitation && this.state.currentStep === 1) {
      if (registrationData.isAdmin) {
        // Skip terms step for admin users
        this.setState({ currentStep: 3, msg: null, loading: false });
      } else {
        if (registrationData.program && registrationData.program._id) {
          this.props.getTermsByProgram(registrationData.program._id);
        }
        this.setState({ currentStep: 2, msg: null, loading: false });
      }
    }

    // Close modal when authentication succeeds (user registered successfully)
    if (this.state.modal && isAuthenticated && !prevProps.isAuthenticated) {
      this.toggle();
    }
  }

  toggle = () => {
    this.props.clearErrors();
    this.props.clearRegistrationData();
    this.setState({
      modal: !this.state.modal,
      currentStep: 1,
      msg: null,
      loading: false
    });
  };

  handleEmailVerification = (email) => {
    this.setState({ msg: null, loading: true });
    this.props.verifyEmail(email);
  };

  handleGroupCodeVerification = (email, groupCode) => {
    this.setState({ msg: null, loading: true });
    this.props.verifyGroupCode(email, groupCode);
  };

  handleTermsAccept = () => {
    this.setState({ currentStep: 3, msg: null });
  };

  handleRegistration = (userData) => {
    const { registrationData } = this.props;
    this.setState({ msg: null, loading: true });
    
    // Include group code if registration was via group code
    const registrationPayload = registrationData.viaGroupCode 
      ? { ...userData, groupCode: registrationData.group?.code }
      : userData;
    
    this.props.register(registrationPayload);
  };

  handleBackToStep1 = () => {
    this.setState({ currentStep: 1, msg: null, loading: false });
    this.props.clearRegistrationData();
  };

  handleBackToStep2 = () => {
    const { registrationData } = this.props;
    if (registrationData.isAdmin) {
      // For admin users, go back to step 1 since step 2 is skipped
      this.setState({ currentStep: 1, msg: null, loading: false });
    } else {
      this.setState({ currentStep: 2, msg: null, loading: false });
    }
  };

  renderCurrentStep() {
    const { currentStep, msg, loading } = this.state;
    const { registrationData, registrationTerms, error } = this.props;

    switch (currentStep) {
      case 1:
        return (
          <EmailStep
            onVerifyEmail={this.handleEmailVerification}
            onVerifyGroupCode={this.handleGroupCodeVerification}
            error={msg}
            loading={loading}
            noInvitation={registrationData.noInvitation}
            email={registrationData.email}
          />
        );
      case 2:
        return (
          <TermsStep
            termsConditions={registrationTerms}
            onAccept={this.handleTermsAccept}
            onBack={this.handleBackToStep1}
            loading={!registrationTerms}
          />
        );
      case 3:
        return (
          <UserDetailsStep
            onRegister={this.handleRegistration}
            onBack={this.handleBackToStep2}
            error={msg}
            loading={loading}
            email={registrationData.email}
          />
        );
      default:
        return null;
    }
  }

  render() {
    const { currentStep } = this.state;
    const { variant = 'navbar', registrationData } = this.props;
    const isAdmin = registrationData && registrationData.isAdmin;
    // Show 2 steps for admin users, 3 steps for regular users
    const totalSteps = isAdmin ? 2 : 3;
    
    const modalContent = this.state.modal ? (
      <div className="fixed inset-0 z-[9999] overflow-auto bg-black bg-opacity-40 flex items-center justify-center">
        <div className="relative bg-white rounded-xl md:max-w-md w-full sm:mx-4 mx-0 p-6 shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Register</h3>
              <div className="flex space-x-2 mt-2">
                {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
                  let isActive = false;
                  if (isAdmin) {
                    // For admin: only 2 steps shown (step 1 = email, step 2 = details)
                    // currentStep 1 = step 1 active, currentStep 3 = step 2 active
                    isActive = (step === 1 && currentStep >= 1) || (step === 2 && currentStep >= 3);
                  } else {
                    // For regular users: normal flow - step 1, 2, 3
                    isActive = step <= currentStep;
                  }
                  return (
                    <div
                      key={step}
                      className={`h-2 w-6 rounded-full ${
                        isActive ? 'bg-[#1086a4]' : 'bg-gray-200'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
            <button 
              onClick={this.toggle} 
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors duration-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {this.renderCurrentStep()}
        </div>
      </div>
    ) : null;

    // Different button styles based on variant
    const buttonClassName = variant === 'home' 
      ? "bg-[#1db5d4] hover:bg-[#16a2c7] text-white font-semibold py-4 px-10 rounded-lg transition-colors duration-200"
      : "text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium";

    return (
      <>
        <button onClick={this.toggle} className={buttonClassName}>
          Register
        </button>
        {modalContent && ReactDOM.createPortal(
          modalContent,
          document.body
        )}
      </>
    );
  }
}

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  error: state.error,
  registrationData: state.auth.registrationData,
  registrationTerms: state.termsConditions.registrationTerms
});

export default connect(
  mapStateToProps,
  { register, verifyEmail, verifyGroupCode, getTermsByProgram, clearErrors, clearRegistrationData }
)(RegisterModal);
